import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useCropperDispatch, actions, stateKeys } from '../../model';
import { useValueRef } from '../../utils/valueRef';
import { fileDataHandler } from '../commonUtils';
import { getCroppedArea, getZoom } from '../../utils/common';

const { addFile } = actions;

const StateContext = createContext();
const ActionsContext = createContext();

export const useInitialBlockState = () => useContext(StateContext);
export const useInitialBlockActions = () => useContext(ActionsContext);

const Provider = ({
  children,
  file,
  initialCroppedArea,
  zoom,
  croppedFile,
  inForm,
  onDelete,
  hasZoom,
}) => {
  const dispatch = useCropperDispatch();

  const canDelete = typeof onDelete === 'function';

  const initialDataRef = useValueRef({
    file,
    initialCroppedArea,
    zoom,
    hasZoom,
  });
  const onDeleteRef = useValueRef(onDelete);

  const croppedFileSrc = useMemo(
    () => (croppedFile ? URL.createObjectURL(croppedFile) : undefined),
    [croppedFile]
  );

  const [prevCroppedFileSrc, setPrevCroppedFileSrc] = useState(croppedFileSrc);
  if (croppedFileSrc !== prevCroppedFileSrc) {
    URL.revokeObjectURL(prevCroppedFileSrc);
    setPrevCroppedFileSrc(croppedFileSrc);
  }

  const onAddFileClick = useCallback(() => {
    dispatch(addFile());
  }, []);

  const onCroppedFileDelete = useCallback(() => {
    if (typeof onDeleteRef.current === 'function') {
      onDeleteRef.current();
    }
  }, []);

  const onChangeCropAreaClick = useCallback(() => {
    const {
      file,
      zoom,
      initialCroppedArea: croppedArea,
      hasZoom,
    } = initialDataRef.current;

    fileDataHandler(
      {
        [stateKeys.file]: file,
        ...(hasZoom ? { [stateKeys.zoom]: zoom } : null),
        [stateKeys.initialCroppedArea]: getCroppedArea(croppedArea),
      },
      dispatch
    );
  }, []);

  const state = useMemo(
    () => ({ src: croppedFileSrc, inForm, canDelete }),
    [croppedFileSrc, inForm, canDelete]
  );

  const actions = useMemo(
    () => ({
      onAddFileClick,
      onChangeCropAreaClick,
      onDelete: onCroppedFileDelete,
    }),
    [onAddFileClick, onChangeCropAreaClick, onCroppedFileDelete]
  );

  return (
    <StateContext.Provider value={state}>
      <ActionsContext.Provider value={actions}>
        {children}
      </ActionsContext.Provider>
    </StateContext.Provider>
  );
};

export default Provider;
